import React, { useState, useEffect } from "react";
import Card from "components/card";
import { MdClose, MdFlightTakeoff } from "react-icons/md";
import { FaPlane, FaClock, FaDollarSign } from "react-icons/fa";

const FlightResultsModal = ({ isOpen, onClose, onSelect, searchData }) => {
  const [loading, setLoading] = useState(true);
  const [flights, setFlights] = useState([]);

  // Mock flight data
  const mockFlights = [
    {
      id: 1,
      name: "AA123",
      airline: "American Airlines",
      route: "JFK → LAX",
      time: "8:00 AM - 11:30 AM",
      duration: "5h 30m",
      price: "$299",
      departureDate: "2024-01-15"
    },
    {
      id: 2,
      name: "UA456",
      airline: "United Airlines", 
      route: "JFK → LAX",
      time: "2:15 PM - 5:45 PM",
      duration: "5h 30m",
      price: "$349",
      departureDate: "2024-01-15"
    },
    {
      id: 3,
      name: "DL789",
      airline: "Delta Airlines",
      route: "JFK → LAX", 
      time: "6:30 PM - 10:00 PM",
      duration: "5h 30m",
      price: "$279",
      departureDate: "2024-01-15"
    },
    {
      id: 4,
      name: "SW101",
      airline: "Southwest Airlines",
      route: "JFK → LAX",
      time: "11:45 AM - 3:15 PM", 
      duration: "5h 30m",
      price: "$199",
      departureDate: "2024-01-15"
    }
  ];

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      // Simulate search delay
      setTimeout(() => {
        setFlights(mockFlights);
        setLoading(false);
      }, 2500);
    }
  }, [isOpen]);

  const handleSelect = (flight) => {
    onSelect(flight);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
      <Card extra="w-[800px] max-h-[90vh] overflow-y-auto bg-white/95 dark:bg-navy-800/95 backdrop-blur-sm border border-white/20 dark:border-navy-600/50 shadow-2xl">
        <div className="bg-[#5a88c6] shadow-lg p-6 rounded-t-[20px] relative overflow-hidden sticky top-0 z-30">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
          <div className="relative z-10 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="rounded-2xl bg-white/20 backdrop-blur-sm p-3">
                <FaPlane className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white">Available Flights</h3>
                <p className="text-white/80 text-sm">
                  {searchData?.from} → {searchData?.to} • {searchData?.departureDate}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="rounded-full bg-white/20 p-2 text-white hover:bg-white/30 transition-colors">
              <MdClose className="h-6 w-6" />
            </button>
          </div>
        </div>
        
        <div className="p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-yellow-500 border-t-transparent mb-4"></div>
              <p className="text-slate-600 dark:text-slate-400 text-lg font-medium">Searching for flights...</p>
              <p className="text-slate-500 dark:text-slate-500 text-sm">Please wait while we find the best options</p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-slate-700 dark:text-white font-semibold mb-4">
                Found {flights.length} flights for your search
              </p>
              {flights.map((flight) => (
                <div
                  key={flight.id}
                  className="group p-6 rounded-2xl bg-gradient-to-r from-white to-slate-50 dark:from-navy-800 dark:to-navy-700 border-2 border-slate-200 dark:border-navy-600 hover:border-yellow-400 dark:hover:border-yellow-400 hover:shadow-xl transition-all duration-300 cursor-pointer"
                  onClick={() => handleSelect(flight)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="p-3 rounded-2xl bg-[#5a88c6] shadow-lg">
                        <MdFlightTakeoff className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h4 className="text-xl font-bold text-slate-800 dark:text-white">{flight.name}</h4>
                        <p className="text-slate-600 dark:text-slate-400">{flight.airline}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-green-600">{flight.price}</p>
                      <p className="text-slate-500 dark:text-slate-400 text-sm">per person</p>
                    </div>
                  </div>
                  
                  <div className="mt-4 grid grid-cols-3 gap-4">
                    <div className="flex items-center space-x-2">
                      <FaPlane className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                      <span className="text-slate-700 dark:text-white font-medium">{flight.route}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <FaClock className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                      <span className="text-slate-700 dark:text-white font-medium">{flight.time}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-slate-500 dark:text-slate-400">⏱️</span>
                      <span className="text-slate-700 dark:text-white font-medium">{flight.duration}</span>
                    </div>
                  </div>
                  
                  <div className="mt-4 flex justify-end">
                    <button className="px-6 py-2 bg-[#5a88c6] text-white rounded-xl font-semibold hover:bg-[#4a7bb6] transition-all duration-200 group-hover:scale-105">
                      Select Flight
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default FlightResultsModal;